import { useState, type FormEvent } from "react";
import type { NewRestroom } from "../types";
import { useTurnstile } from "../lib/useTurnstile";
import { useT } from "../i18n";

// Zapéká se při buildu (VITE_*). Když chybí, Turnstile se nevyrenderuje a
// formulář nejde odeslat — proto to říkáme nahlas místo mrtvého tlačítka.
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

interface Props {
  lat: number;
  lng: number;
  onSubmit: (data: NewRestroom, turnstileToken: string) => Promise<void>;
  onCancel: () => void;
}

export default function AddRestroomForm({ lat, lng, onSubmit, onCancel }: Props) {
  const t = useT();
  const [name, setName] = useState("");
  const [wheelchair, setWheelchair] = useState(false);
  const [fee, setFee] = useState(false);
  const [changingTable, setChangingTable] = useState(false);
  const [openingHours, setOpeningHours] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const { containerRef, reset } = useTurnstile({
    sitekey: SITE_KEY,
    onToken: setToken,
    onExpire: () => setToken(null),
  });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(
        {
          name: name || undefined,
          lat,
          lng,
          wheelchair,
          fee,
          changingTable,
          openingHours: openingHours || undefined,
          note: note || undefined,
        },
        token,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t("form.error"));
      setSaving(false);
      setToken(null);
      reset();
    }
  }

  return (
    <form className="add-form" onSubmit={handleSubmit}>
      <h2>{t("form.title")}</h2>
      <p className="coords">
        {lat.toFixed(5)}, {lng.toFixed(5)}
      </p>

      <label>
        {t("form.name")}
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("form.namePlaceholder")}
        />
      </label>

      <label className="check">
        <input type="checkbox" checked={wheelchair} onChange={(e) => setWheelchair(e.target.checked)} />
        {t("form.wheelchair")}
      </label>
      <label className="check">
        <input type="checkbox" checked={fee} onChange={(e) => setFee(e.target.checked)} />
        {t("form.fee")}
      </label>
      <label className="check">
        <input type="checkbox" checked={changingTable} onChange={(e) => setChangingTable(e.target.checked)} />
        {t("form.changingTable")}
      </label>

      <label>
        {t("form.openingHours")}
        <input
          value={openingHours}
          onChange={(e) => setOpeningHours(e.target.value)}
          placeholder={t("form.openingHoursPlaceholder")}
        />
      </label>

      <label>
        {t("form.note")}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </label>

      {SITE_KEY ? (
        <div ref={containerRef} className="turnstile" />
      ) : (
        <p className="error">{t("form.missingSiteKey")}</p>
      )}

      {error && <p className="error">{error}</p>}

      <div className="actions">
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>
          {t("form.cancel")}
        </button>
        <button type="submit" className="btn primary" disabled={saving || !token}>
          {saving ? t("form.saving") : t("form.save")}
        </button>
      </div>
    </form>
  );
}
